import { useMemo, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Loader from '@/components/Loader';
import RefashionLogo from '@/components/RefashionLogo';
import Badge from '@/design_system/Badge';
import Button from '@/design_system/Button';
import { InputSearchSelect } from '@/design_system/InputSearchSelect/InputSearchSelect';
import Stack from '@/design_system/Stack';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconChevron from '@/icons/Chevron.svg';
import { Brand, useBrands } from '@/models/brand';
import {
  ProductAttribute,
  ProductAttributeValueWithBrand,
  useProductAttributes,
} from '@/models/productAttribute';
import { useDatabaseTranslation } from '@/services/i18n';
import { createBEMClasses } from '@/utils/classname';

import { useSettingsScope } from '../../../../../SettingsContext';

import './ProductAttributes.css';

const { block, element } = createBEMClasses('product-attributes-tree');

const ROOT_PARENT_KEY = '__root__';

type AttributesByParentValueId = Record<string, ProductAttribute[]>;
type ValuesByAttributeId = Record<string, ProductAttributeValueWithBrand[]>;

export const ProductAttributes = () => {
  const { t } = useLingui();
  const { _compare } = useDatabaseTranslation();

  const { scope } = useSettingsScope();

  const organizationId = scope?.scope === 'organization' ? scope.organizationId : undefined;

  const {
    data: { productAttributes = [] } = { productAttributes: [] },
    isLoading: isProductAttributesLoading,
    isError: isProductAttributesError,
    error: productAttributesError,
  } = useProductAttributes({ organizationId });

  const {
    data: { brands = [] } = { brands: [] },
    isLoading: isBrandsLoading,
    isError: isBrandsError,
    error: brandsError,
  } = useBrands({ organizationId, limit: 500, offset: 0 });

  const [selectedBrandId, setSelectedBrandId] = useState<string | undefined>(undefined);

  const { attributesByParentValueId, valuesByAttributeId, rootAttributes } = useMemo(() => {
    const nextAttributesByParentValueId: AttributesByParentValueId = {};
    const nextValuesByAttributeId: ValuesByAttributeId = {};

    const filteredProductAttributes = productAttributes.filter((attribute) =>
      attribute.productAttributeValues.some(
        (value) =>
          !selectedBrandId ||
          value.brands.length === 0 ||
          value.brands.some((brand) => brand.id === selectedBrandId)
      )
    );

    for (const attribute of filteredProductAttributes) {
      const parentKey = attribute.parentProductAttributeValueId ?? ROOT_PARENT_KEY;

      nextAttributesByParentValueId[parentKey] = [
        ...(nextAttributesByParentValueId[parentKey] ?? []),
        attribute,
      ];
      nextValuesByAttributeId[attribute.id] = [...attribute.productAttributeValues].filter(
        (value) =>
          !selectedBrandId ||
          value.brands.length === 0 ||
          value.brands.some((brand) => brand.id === selectedBrandId)
      );
    }

    for (const key of Object.keys(nextAttributesByParentValueId)) {
      nextAttributesByParentValueId[key] = nextAttributesByParentValueId[key].toSorted((a, b) => {
        if (a.sortOrder === b.sortOrder) {
          return _compare(a.name, b.name);
        }

        return a.sortOrder - b.sortOrder;
      });
    }

    for (const key of Object.keys(nextValuesByAttributeId)) {
      nextValuesByAttributeId[key] = nextValuesByAttributeId[key].toSorted((a, b) => {
        if (a.sortOrder === b.sortOrder) {
          return _compare(a.name, b.name);
        }

        if (a.sortOrder === null) {
          return 1;
        }

        if (b.sortOrder === null) {
          return -1;
        }

        return a.sortOrder - b.sortOrder;
      });
    }

    return {
      attributesByParentValueId: nextAttributesByParentValueId,
      valuesByAttributeId: nextValuesByAttributeId,
      rootAttributes: nextAttributesByParentValueId[ROOT_PARENT_KEY] ?? [],
    };
  }, [_compare, productAttributes, selectedBrandId]);

  if (!organizationId) {
    return (
      <Paragraph>
        <Trans id="settings.product-attributes.no-data">No product attribute values found</Trans>
      </Paragraph>
    );
  }

  if (isProductAttributesLoading || isBrandsLoading) {
    return <Loader />;
  }

  if (isProductAttributesError || isBrandsError) {
    const error = isProductAttributesError ? productAttributesError : brandsError;
    const errorMessage =
      error instanceof Error
        ? error.message
        : t({ id: '_general.error.unknown', message: 'Unknown error' });

    return (
      <Paragraph>
        <Trans id="settings.product-attributes.error">An error has occurred: {errorMessage}</Trans>
      </Paragraph>
    );
  }

  return (
    <main>
      <Stack gap="1rem" className={block()}>
        <Title
          as="h2"
          size="200"
          weight="medium"
          style={{
            display: 'inline-flex',
            gap: '1rem',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Trans id="settings.organization.data-catalogs.product-attributes.title">
            Product attributes
          </Trans>

          {brands.length > 1 && (
            <InputSearchSelect
              size="small"
              style={{ flexBasis: '100px', flexGrow: '1', maxWidth: '200px' }}
              options={brands}
              getOptionLabel={(brand) => brand.name}
              getOptionValue={(brand) => brand.id}
              onChange={(brand) => {
                setSelectedBrandId(brand?.id);
              }}
              value={
                selectedBrandId ? brands.find((brand) => brand.id === selectedBrandId) : undefined
              }
              placeholder={t({
                id: 'settings.organization.data-catalogs.product-attributes.filter-by-brand.label',
                message: 'Filter by brand',
              })}
              aria-label={t({
                id: 'settings.organization.data-catalogs.product-attributes.filter-by-brand.label',
                message: 'Filter by brand',
              })}
            />
          )}
        </Title>

        {rootAttributes.length === 0 ? (
          <Paragraph>
            <Trans id="settings.product-attributes.no-data">
              No product attribute values found
            </Trans>
          </Paragraph>
        ) : (
          rootAttributes.map((attribute) => (
            <ProductAttributeNode
              key={attribute.id}
              attribute={attribute}
              depth={0}
              attributesByParentValueId={attributesByParentValueId}
              valuesByAttributeId={valuesByAttributeId}
              brands={brands}
            />
          ))
        )}
      </Stack>
    </main>
  );
};

const ProductAttributeNode = ({
  attribute,
  depth,
  attributesByParentValueId,
  valuesByAttributeId,
  brands,
}: {
  attribute: ProductAttribute;
  depth: number;
  attributesByParentValueId: AttributesByParentValueId;
  valuesByAttributeId: ValuesByAttributeId;
  brands: Brand[];
}) => {
  const { _db } = useDatabaseTranslation();
  const [isOpen, setIsOpen] = useState(depth === 0);
  const values = valuesByAttributeId[attribute.id] ?? [];
  const hasValues = values.length > 0;

  return (
    <div className={element('node', { attribute: true, root: depth === 0 })}>
      <Button
        variant="style-less"
        onPress={() => setIsOpen((prev) => !prev)}
        ariaLabel={_db(attribute.name)}
      >
        <IconChevron down={!isOpen} up={isOpen} />
        <Paragraph size={depth === 0 ? '50' : '100'} weight="medium">
          {_db(attribute.name)}
        </Paragraph>
        {attribute.required && (
          <Badge color="primary" hasBorder>
            <Trans id="settings.product-attributes.required">Required</Trans>
          </Badge>
        )}
        {!attribute.enabled && (
          <Badge color="neutral">
            <Trans id="settings.product-attributes.disabled">Disabled</Trans>
          </Badge>
        )}
      </Button>

      {isOpen && (
        <Stack gap="0.5rem" className={element('children')}>
          {values.map((value) => (
            <ProductAttributeValueNode
              key={value.id}
              value={value}
              depth={depth + 1}
              attributesByParentValueId={attributesByParentValueId}
              valuesByAttributeId={valuesByAttributeId}
              brands={brands}
            />
          ))}
          {!hasValues && (
            <Paragraph>
              <Trans id="settings.product-attributes.no-values">No children values</Trans>
            </Paragraph>
          )}
        </Stack>
      )}
    </div>
  );
};

const ProductAttributeValueNode = ({
  value,
  depth,
  attributesByParentValueId,
  valuesByAttributeId,
  brands,
}: {
  value: ProductAttributeValueWithBrand;
  depth: number;
  attributesByParentValueId: AttributesByParentValueId;
  valuesByAttributeId: ValuesByAttributeId;
  brands: Brand[];
}) => {
  const { _db } = useDatabaseTranslation();
  const childAttributes = attributesByParentValueId[value.id] ?? [];

  return (
    <div className={element('node', { value: true })}>
      <Stack gap="0.5rem" row flexWrap="nowrap">
        <ProductAttributeValueIllustration value={value} />
        <Stack>
          <Stack row gap="0.5rem">
            <Paragraph size="100" weight="heavy">
              {_db(value.name)}
            </Paragraph>
            {!value.isSelectable && (
              <Badge color="neutral">
                <Trans id="settings.product-attributes.unselectable">Unselectable</Trans>
              </Badge>
            )}
            {value.refashionId && <RefashionLogo tooltip={value.refashionId ?? undefined} />}
            {value.warrantyDurationInMonths !== null && (
              <Badge color="tertiary">
                <Trans id="settings.product-attributes.warranty-duration-in-months">
                  {value.warrantyDurationInMonths} months of warranty
                </Trans>
              </Badge>
            )}
          </Stack>
          {brands.length > 1 && (
            <>
              <Stack row gap="0.25rem" alignItems="baseline">
                <Paragraph size="200" weight="bold" color="secondary">
                  <Trans id="settings.product-attributes.brands">Brands:</Trans>
                </Paragraph>

                <Paragraph size="200" weight="regular" color="secondary">
                  {value.brands.length === 0 && (
                    <Trans id="settings.product-attributes.all-brands">All</Trans>
                  )}
                  {value.brands
                    .slice(0, 5)
                    .map((brand) => brand.name)
                    .join(', ')}
                </Paragraph>

                {value.brands.length > 5 && (
                  <>
                    {', '}
                    <Tooltip
                      content={value.brands
                        .slice(5)
                        .map((brand) => brand.name)
                        .join(', ')}
                    >
                      <Button variant="style-less" size="small">
                        +{value.brands.length - 5}
                      </Button>
                    </Tooltip>
                  </>
                )}
              </Stack>
            </>
          )}
          {!!value.colors?.length && (
            <Stack row alignItems="center">
              <Paragraph size="200" weight="regular" color="secondary">
                <InlineText size="200" weight="bold" color="secondary">
                  <Trans id="settings.product-attributes.colors">Colors:</Trans>
                </InlineText>
                {value.colors.slice(0, 5).map(_db).join(', ')}
              </Paragraph>
              {value.colors.length > 5 && (
                <Tooltip content={value.colors.slice(5).map(_db).join(', ')}>
                  <Button variant="style-less" size="small">
                    , +{value.colors.length - 5}
                  </Button>
                </Tooltip>
              )}
            </Stack>
          )}
          {!!value.sizes?.length && (
            <Stack row alignItems="center">
              <Paragraph size="200" weight="regular" color="secondary">
                <InlineText size="200" weight="bold" color="secondary">
                  <Trans id="settings.product-attributes.sizes">Sizes:</Trans>
                </InlineText>
                {value.sizes.slice(0, 5).map(_db).join(', ')}
              </Paragraph>
              {value.sizes.length > 5 && (
                <Tooltip content={value.sizes.slice(5).map(_db).join(', ')}>
                  <Button variant="style-less" size="small">
                    , +{value.sizes.length - 5}
                  </Button>
                </Tooltip>
              )}
            </Stack>
          )}
          {value.weightUnit && (
            <Paragraph size="200" weight="regular" color="secondary">
              <InlineText size="200" weight="bold" color="secondary">
                <Trans id="settings.product-attributes.weight-unit">Weight unit:</Trans>
              </InlineText>{' '}
              {value.weightUnit}
            </Paragraph>
          )}
        </Stack>
      </Stack>

      {childAttributes.map((attribute) => (
        <ProductAttributeNode
          key={attribute.id}
          attribute={attribute}
          depth={depth + 1}
          attributesByParentValueId={attributesByParentValueId}
          valuesByAttributeId={valuesByAttributeId}
          brands={brands}
        />
      ))}
    </div>
  );
};

const ProductAttributeValueIllustration = ({
  value,
}: {
  value: ProductAttributeValueWithBrand;
}) => {
  if (!value.illustration) {
    return null;
  }

  if (value.illustration.url) {
    return (
      <img
        className={element('value-illustration')}
        src={value.illustration.url}
        alt=""
        style={{
          backgroundColor: value.illustration.backgroundColor,
        }}
      />
    );
  }

  if (value.illustration.backgroundUrl) {
    return (
      <div
        className={element('value-illustration')}
        style={{
          backgroundImage: `url(${value.illustration.backgroundUrl})`,
        }}
      />
    );
  }

  return null;
};
